// API Configuration — single env var: VITE_API_BASE_URL
//
// - Leave unset (or empty): use `/api/tprm` (Vite dev proxy → Django, or nginx same-origin in prod).
// - Set to `proxy` or `same-origin`: same as above, explicit.
// - Set to a full URL: e.g. http://127.0.0.1:8000/api/tprm or https://your-host/api/tprm

import { shouldAvoidLoginRedirect, requestAuthFromParent } from '@/utils/embedContext'
import { tokenService } from '@/services/tokenService'

const stripTrailingSlash = (value = '') => value.replace(/\/+$/, '')

const raw = (import.meta.env.VITE_API_BASE_URL || '').trim()
const lower = raw.toLowerCase()
const useRelative = !raw || lower === 'proxy' || lower === 'same-origin'

const BASE_URL = useRelative ? '/api/tprm' : stripTrailingSlash(raw)

const API_CONFIG = {
  BASE_URL,
  RFP_APPROVAL_BASE: `${BASE_URL}/rfp-approval`,
  TIMEOUT: parseInt(import.meta.env.VITE_API_TIMEOUT || '60000', 10),
}

if (import.meta.env.DEV) {

  if (String(API_CONFIG.BASE_URL).startsWith('/')) {

  }
}

const API_ENDPOINTS = {
  RFP_APPROVAL: {
    WORKFLOWS: '/workflows/',
    USERS: '/users/',
    REQUESTS: '/requests/',
    STAGES: '/stages/',
    COMMENTS: '/comments/',
    USER_APPROVALS: '/user-approvals/',
    UPDATE_STAGE_STATUS: '/update-stage-status/',
  },
  CREATE_ACCESS_REQUEST: `${API_CONFIG.BASE_URL}/rbac/access-requests/`,
  GET_ACCESS_REQUESTS: (userId) => `${API_CONFIG.BASE_URL}/rbac/access-requests/${userId}/`,
  UPDATE_ACCESS_REQUEST_STATUS: (requestId) => `${API_CONFIG.BASE_URL}/rbac/access-requests/${requestId}/status/`,
}

const buildApiUrl = (endpoint, baseUrl = API_CONFIG.RFP_APPROVAL_BASE) => {
  return `${baseUrl}${endpoint}`
}

const getAuthToken = () => {
  return (
    tokenService.getToken() ||
    localStorage.getItem('session_token') ||
    localStorage.getItem('auth_token') ||
    localStorage.getItem('access_token')
  )
}

const apiCall = async (url, options = {}) => {
  const token = getAuthToken()
  const { skipRedirect = false, ...fetchOptions } = options

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    ...fetchOptions,
  }

  try {
    const response = await fetch(url, defaultOptions)

    if (response.status === 401) {

      if (!skipRedirect) {
        if (shouldAvoidLoginRedirect()) {
          requestAuthFromParent()
        } else {
          tokenService.clearTokens()
          localStorage.removeItem('session_token')
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          localStorage.removeItem('current_user')
          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
            return new Promise(() => {})
          }
        }
      }
      throw new Error('Authentication required')
    }

    if (response.status === 403) {

      const errorData = await response.json().catch(() => ({}))
      const errorMessage =
        errorData?.error || errorData?.message || 'You do not have permission to access this resource.'
      const errorCode = errorData?.code || '403'

      if (!skipRedirect) {
        sessionStorage.setItem(
          'access_denied_error',
          JSON.stringify({
            message: errorMessage,
            code: errorCode,
            timestamp: new Date().toISOString(),
            path: window.location.pathname,
          })
        )
        if (!shouldAvoidLoginRedirect() && window.location.pathname !== '/access-denied') {

          window.location.href = '/access-denied'
          return new Promise(() => {})
        }
      }
      throw new Error(errorMessage)
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      return await response.json()
    }
    const text = await response.text()

    throw new Error(`Server returned non-JSON response. Status: ${response.status}`)
  } catch (error) {

    throw error
  }
}

export const API_BASE_URL = BASE_URL

export function getApiBaseUrl() {
  return BASE_URL
}

export {
  API_CONFIG,
  API_ENDPOINTS,
  buildApiUrl,
  apiCall,
  getAuthToken,
}
